import classNames from "classnames"
import { format } from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"

import NumberedCircle from "src/components/NumberedCircle"
import OverflowMenu from "src/components/OverflowMenu"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { titlecase } from "src/utils/string_helpers"

import CruiseStopIcon from "./CruiseStopIcon"
import editCruiseStopLink from "./cruiseStopLink"
import displayDuration from "./displayDuration"

const CruiseStopItemMobile = ({
  stop,
  previousStop,
  cruisePlanId,
  handleMoveStop,
  index,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const renderPreviousStopDateErrors = () => {
    if (
      previousStop &&
      previousStop.endDate &&
      stop.startDate &&
      format(new Date(previousStop.endDate), "yyyy-MM-dd") !==
        format(new Date(stop.startDate), "yyyy-MM-dd")
    ) {
      return (
        <i
          className="fa-solid fa-triangle-exclamation px-1 text-red-500"
          data-toggle="tooltip"
          data-placement="top"
          title="Possible date issue compared to previous stop end date"
        />
      )
    }
  }

  const timeOf = (time, startOrEnd) => {
    if (!time) return null
    const isStart = startOrEnd === "start"

    if (stop.reservation) {
      return (
        <span className="whitespace-nowrap">
          {isStart ? `after ${time}` : `before ${time}`}
          <i
            className="fa-solid fa-circle-info px-1 text-gray-500"
            title={`Marina ${isStart ? "check-in" : "check-out"} time`}
            data-toggle="tooltip"
            data-placement="top"
          />
        </span>
      )
    } else {
      return <span className="whitespace-nowrap">{time}</span>
    }
  }

  return (
    <div
      className="mb-4 flex cursor-pointer flex-col rounded-xl border border-gray-100 bg-white p-4 shadow"
      onClick={(e) => {
        // Don't expand if clicking on the expand button or any interactive elements
        if (e.target.closest('button, a, [role="button"]')) return
        setIsExpanded(!isExpanded)
      }}
    >
      {/* Header - Always visible */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex-shrink-0">
            <NumberedCircle number={index + 1} className="shadow" />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <CruiseStopIcon stopType={stop.type} />
            <div className="min-w-0">
              <div className="truncate font-semibold text-gray-900">
                {stop.marina ? (
                  <a
                    href={`/explore/destination/${stop.marina.mcomId}`}
                    target="_blank"
                    className="text-link"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {stop.name}
                  </a>
                ) : (
                  stop.name
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stop.reservation && (
            <span className="inline-block rounded bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-900">
              {titlecase(stop.reservation.status.replace(/_/g, " "))}
            </span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
            className={classNames(
              "bg-gray-50 size-8 rounded-full border border-gray-200 p-1 transition hover:bg-gray-100",
              isExpanded ? "text-blue-600" : "text-gray-500"
            )}
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
          >
            <i
              className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"}`}
            />
          </button>
        </div>
      </div>

      {/* Dates & Times - Always visible */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-600">
        {stop.startDate && (
          <span className="font-medium">
            {format(new Date(stop.startDate), "MMM dd, yyyy")}
            {renderPreviousStopDateErrors()}
          </span>
        )}
        {stop.startDate && stop.endDate && (
          <span className="text-gray-400">-</span>
        )}
        {stop.endDate && (
          <span className="font-medium">
            {format(new Date(stop.endDate), "MMM dd, yyyy")}
          </span>
        )}
        {(stop.arrivalTime || stop.departureTime) && (
          <span className="ml-2 flex gap-2 text-xs text-gray-500">
            {stop.arrivalTime && (
              <span>Arr: {timeOf(stop.arrivalTime, "start")}</span>
            )}
            {stop.departureTime && (
              <span>Dep: {timeOf(stop.departureTime, "end")}</span>
            )}
          </span>
        )}
      </div>

      {/* Note preview - Always visible if exists */}
      {stop.note && (
        <div className="mt-2 max-w-full truncate text-sm text-gray-700">
          {stop.note.length > 90 ? stop.note.slice(0, 90) + "..." : stop.note}
        </div>
      )}

      {/* Expandable content */}
      {isExpanded && (
        <div
          className="mt-4 space-y-3 border-t pt-3"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Actions */}
          <div className="float-right">
            <OverflowMenu>
              <OverflowMenu.Item
                onClick={(e) => {
                  e.stopPropagation()
                  handleMoveStop(stop.id, "up")
                }}
              >
                Move Up
              </OverflowMenu.Item>
              <OverflowMenu.Item
                onClick={(e) => {
                  e.stopPropagation()
                  handleMoveStop(stop.id, "down")
                }}
              >
                Move Down
              </OverflowMenu.Item>
              <OverflowMenu.Item>
                <a
                  href={editCruiseStopLink(stop, cruisePlanId)}
                  onClick={(e) => e.stopPropagation()}
                >
                  Edit
                </a>
              </OverflowMenu.Item>
              <OverflowMenu.Item>
                <a
                  href={`/account/cruise_plans/${cruisePlanId}/cruise_stops/${stop.id}`}
                  data-method="delete"
                  data-confirm="Are you sure you want to delete this stop?"
                  onClick={(e) => e.stopPropagation()}
                >
                  Delete
                </a>
              </OverflowMenu.Item>
            </OverflowMenu>
          </div>

          {/* Travel details */}
          {(stop.nauticalMiles || stop.knots || stop.duration) && (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-700">
                Travel Details
              </div>
              <div className="flex flex-col gap-1 text-sm text-gray-600">
                {stop.nauticalMiles && (
                  <div>Distance: {stop.nauticalMiles} nm</div>
                )}
                {stop.knots && <div>Speed: {stop.knots} knots</div>}
                {stop.duration && (
                  <div>Duration: {displayDuration(stop.duration)}</div>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          {(stop.latitude || stop.longitude) && (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-700">Location</div>
              <div className="text-sm text-gray-600">
                {stop.latitude}, {stop.longitude}
              </div>
            </div>
          )}

          {/* Price */}
          {stop.reservation?.totalPrice && (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-700">Price</div>
              <div className="text-sm text-gray-600">
                {formatCurrencyFromMinorUnits({
                  amount: stop.reservation.totalPrice,
                })}
              </div>
            </div>
          )}

          {/* Notes (full) */}
          {stop.note && (
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium text-gray-700">Notes</div>
              <div className="whitespace-pre-line text-sm text-gray-600">
                {stop.note}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

CruiseStopItemMobile.propTypes = {
  stop: PropTypes.shape({
    id: PropTypes.string.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    arrivalTime: PropTypes.string,
    departureTime: PropTypes.string,
    type: PropTypes.string.isRequired,
    name: PropTypes.string,
    note: PropTypes.string,
    marina: PropTypes.shape({
      mcomId: PropTypes.string,
      name: PropTypes.string,
    }),
    title: PropTypes.string,
    reservation: PropTypes.shape({
      status: PropTypes.string,
      totalPrice: PropTypes.number,
    }),
    nauticalMiles: PropTypes.string,
    knots: PropTypes.string,
    duration: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }).isRequired,
  previousStop: PropTypes.shape({
    endDate: PropTypes.string,
  }),
  cruisePlanId: PropTypes.string.isRequired,
  handleMoveStop: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
}

export default CruiseStopItemMobile
